package com.tregz.tremenz.io.model

import com.tregz.tremenz.io.cloud.CloudPath
import com.tregz.tremenz.ui.icon.IconKey
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update

/**
 * The in-memory catalogue. Every collection lives behind a [MutableStateFlow] holding an immutable
 * snapshot, and every change goes through [update], so loaders on IO threads and the UI on the
 * main thread never see a half-written list. The old public mutable lists are now read-only views
 * of the latest snapshot.
 */
object ModelRepository {

    val APERO = ModelData.Section(CloudPath.APERO.toString(), IconKey.WINE)
    val BISTRO = ModelData.Section(CloudPath.BISTRO.toString(), IconKey.BEER)
    val CLUB = ModelData.Section(CloudPath.CLUB.toString(), IconKey.COCKTAIL)

    private val sections: List<ModelData> = listOf(APERO, BISTRO, CLUB)

    /** A media item's identity: the same document id can exist in more than one collection. */
    private data class MediaKey(val isAudio: Boolean, val path: String, val id: String)

    private fun ModelData.Media.key() = MediaKey(this is ModelData.Audio, path.lowercase(), id)

    private val live = MutableStateFlow<List<ModelData>>(emptyList())
    val LIVE: StateFlow<List<ModelData>> = live.asStateFlow()

    /** The latest mixes snapshot, sections first. Read-only: change it through [setMixes] and [add]. */
    val ITEMS: List<ModelData> get() = live.value

    /** Keys the bundled `mixes.json` last supplied, so a later bundle can retire entries it dropped. */
    private val bundledKeys = MutableStateFlow<Set<MediaKey>>(emptySet())

    /** Keys the cloud has supplied. Always written before the item itself, see [add]. */
    private val cloudKeys = MutableStateFlow<Set<MediaKey>>(emptySet())

    private val songsLive = MutableStateFlow<List<ModelData.Song>>(emptyList())
    val SONGS_LIVE: StateFlow<List<ModelData.Song>> = songsLive.asStateFlow()

    /** The latest songs snapshot. Read-only: change it through [setSongs]. */
    val SONGS: List<ModelData.Song> get() = songsLive.value

    private val channelsLive =
        MutableStateFlow<Map<String, Map<String, List<ModelData.Song>>>>(emptyMap())

    /** The hourly channel programming, keyed by channel name and then by its a-z hour bucket. */
    val CHANNELS_LIVE: StateFlow<Map<String, Map<String, List<ModelData.Song>>>> =
        channelsLive.asStateFlow()

    /** The latest programming snapshot. Read-only: change it through [setChannels]. */
    val CHANNELS_DATA: Map<String, Map<String, List<ModelData.Song>>> get() = channelsLive.value

    private val activeSongs = MutableStateFlow<List<ModelData.Song>>(emptyList())

    /**
     * The queue continuous playback walks. It is the whole [SONGS] catalogue by default and the
     * current hour of one channel while that channel plays, so the hosts can advance through either
     * without knowing which one the user started. Replaced as one immutable list, never edited.
     */
    val ACTIVE_SONGS: List<ModelData.Song> get() = activeSongs.value

    private val activeChannel = MutableStateFlow<String?>(null)

    /** The channel feeding [ACTIVE_SONGS], or null when the queue is the whole catalogue. */
    val ACTIVE_CHANNEL: StateFlow<String?> = activeChannel.asStateFlow()

    private val mixesStatus = MutableStateFlow(ModelStatus.LOADING)
    private val songsStatus = MutableStateFlow(ModelStatus.LOADING)
    private val channelsStatus = MutableStateFlow(ModelStatus.LOADING)

    val MIXES_STATUS: StateFlow<ModelStatus> = mixesStatus.asStateFlow()
    val SONGS_STATUS: StateFlow<ModelStatus> = songsStatus.asStateFlow()
    val CHANNELS_STATUS: StateFlow<ModelStatus> = channelsStatus.asStateFlow()

    private val retries = MutableStateFlow<Map<String, () -> Unit>>(emptyMap())

    /** The mixes catalogue has two sources (bundle, cloud); a failure never hides what the other gave. */
    fun reportMixes(status: ModelStatus) = report(mixesStatus, status)

    fun reportSongs(status: ModelStatus) = report(songsStatus, status)

    fun reportChannels(status: ModelStatus) = report(channelsStatus, status)

    private fun report(flow: MutableStateFlow<ModelStatus>, status: ModelStatus) {
        // Once a source has answered, a later failure of a sibling source must not undo that.
        flow.update { current ->
            if (current == ModelStatus.LOADED && status != ModelStatus.LOADED) current else status
        }
    }

    /** Lets a platform loader say how to run again, so a Failed tab can offer a retry. */
    fun registerRetry(name: String, action: () -> Unit) {
        retries.update { it + (name to action) }
    }

    /** Runs every registered loader again. Safe to call while one is in flight: they guard themselves. */
    fun retry() {
        for (action in retries.value.values) action()
    }

    /**
     * Populates the mixes catalogue from the bundled payload and republishes. Entries a previous
     * bundle supplied and this one dropped are pruned, unless the cloud has since supplied them.
     */
    fun setMixes(pairs: List<Pair<ModelData.Audio?, ModelData.Video>>) {
        val incoming: List<ModelData.Media> = pairs.flatMap { listOfNotNull<ModelData.Media>(it.first, it.second) }
        val incomingKeys = incoming.map { it.key() }.toSet()
        val retired = bundledKeys.value - incomingKeys
        bundledKeys.value = incomingKeys
        live.update { current ->
            // Read inside the update so a cloud item inserted since the snapshot is protected.
            val cloud = cloudKeys.value
            val kept = withSections(current).filterNot {
                it is ModelData.Media && it.key() in retired && it.key() !in cloud
            }
            upsert(kept, incoming) { key -> key !in cloud }
        }
        report(mixesStatus, ModelStatus.LOADED)
    }

    /** Replaces the cached songs and republishes them to observers. */
    fun setSongs(songs: List<ModelData.Song>) {
        songsLive.value = songs.toList()
        // A channel that is playing keeps its own queue; only the idle queue follows the catalogue.
        if (activeChannel.value == null) activeSongs.value = songsLive.value
        report(songsStatus, ModelStatus.LOADED)
    }

    /** Republishes the cached songs without re-reading them. */
    fun publishSongs() {
        songsLive.update { it.toList() }
    }

    /** Replaces the cached channel programming and republishes it to observers. */
    fun setChannels(channels: Map<String, Map<String, List<ModelData.Song>>>) {
        channelsLive.value = channels.mapValues { (_, buckets) -> buckets.mapValues { it.value.toList() } }
        report(channelsStatus, ModelStatus.LOADED)
    }

    /**
     * Points continuous playback at [songs]. [channel] names the channel they came from so the
     * Channels tab can show which one is on air; null means the queue is the whole catalogue.
     */
    fun setActiveSongs(songs: List<ModelData.Song>, channel: String? = null) {
        // The list is swapped whole: a reader sees the old queue or the new one, never a gap.
        activeSongs.value = songs.toList()
        activeChannel.value = channel
    }

    /** Back to the initial state: the three sections, nothing cached, nothing on air. */
    fun clear() {
        bundledKeys.value = emptySet()
        cloudKeys.value = emptySet()
        live.value = sections
        songsLive.value = emptyList()
        channelsLive.value = emptyMap()
        activeSongs.value = emptyList()
        activeChannel.value = null
        mixesStatus.value = ModelStatus.LOADING
        songsStatus.value = ModelStatus.LOADING
        channelsStatus.value = ModelStatus.LOADING
    }

    /**
     * Adds or updates a pair in the catalogue. Resolving [posterUrl] needs cloud storage, so
     * the caller — the platform [com.tregz.tremenz.io.cloud.CloudSource] — supplies it, or null
     * when the lookup failed, in which case the poster the item already had is kept.
     */
    fun add(audio: ModelData.Audio?, video: ModelData.Video, posterUrl: String?) {
        val withPoster = listOfNotNull<ModelData.Media>(
            audio?.let { it.copy(posterUrl = posterUrl ?: it.posterUrl) },
            video.copy(posterUrl = posterUrl ?: video.posterUrl)
        )
        // Marked as cloud supplied before it lands, so a concurrent setMixes prune cannot drop it.
        cloudKeys.update { it + withPoster.map { media -> media.key() } }
        live.update { upsert(withSections(it), withPoster) { true } }
        report(mixesStatus, ModelStatus.LOADED)
    }

    private fun withSections(items: List<ModelData>): List<ModelData> =
        if (items.any { it is ModelData.Section }) items else sections + items

    /**
     * Folds [incoming] into [items] in one pass: an existing entry with the same key is replaced in
     * place when [replace] agrees, anything new is appended.
     */
    private fun upsert(
        items: List<ModelData>,
        incoming: List<ModelData.Media>,
        replace: (MediaKey) -> Boolean
    ): List<ModelData> {
        val result = items.toMutableList()
        val index = HashMap<MediaKey, Int>()
        result.forEachIndexed { i, item -> if (item is ModelData.Media) index[item.key()] = i }
        for (media in incoming) {
            val key = media.key()
            val at = index[key]
            if (at == null) {
                index[key] = result.size
                result.add(media)
            } else if (replace(key)) {
                result[at] = media
            }
        }
        return result
    }
}
